import { useState } from 'react';
import {
  shipCancelTruck,
  shipClose,
  shipOverview,
  shipStart,
  type PendingTruck,
  type ShipHead,
} from './logistics-api';
import { TruckModal } from './TruckModal';
import { TruckEditModal } from './TruckEditModal';
import { useData } from './useData';

// Коды ошибок сервера → понятный текст для оператора склада.
const SHIP_ERRORS: Record<string, string> = {
  already_started: 'Отгрузка по этому контракту уже собрана.',
  nothing_loaded: 'Сначала отгрузите хотя бы одну позицию.',
  not_complete: 'Комплектность ещё не 100% — отгрузите остатки.',
  no_truck: 'Отправьте хотя бы одну машину на КПП.',
  kpp_pending: 'Дождитесь выпуска всех машин на КПП.',
  not_found: 'Объект не найден — обновите страницу.',
};

export function ShipScreen() {
  const { data, refresh } = useData('ship', shipOverview);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [truck, setTruck] = useState<ShipHead | null>(null); // окно «Отправить машину»
  const [editTruck, setEditTruck] = useState<{ dealId: number; truck: PendingTruck } | null>(null);

  function cancelTruck(dealId: number, t: PendingTruck) {
    if (window.confirm(`Отменить машину «${t.label}»? Количества вернутся в остаток.`))
      act(shipCancelTruck(dealId, t.id));
  }

  async function act(p: Promise<unknown>) {
    setBusy(true);
    setMsg(null);
    try {
      await p;
      await refresh();
    } catch (e) {
      const code = e instanceof Error ? e.message : '';
      setMsg(SHIP_ERRORS[code] ?? 'Не удалось выполнить действие.');
    } finally {
      setBusy(false);
    }
  }

  if (!data) return <p className="muted">Загрузка…</p>;

  return (
    <div className="scr">
      <div className="scr-head">
        <h2>Склад готовой продукции и отгрузка</h2>
        <span className="muted">комплектность · отгрузка частями</span>
      </div>

      {data.ready.length > 0 && (
        <div
          className="glass"
          style={{
            padding: 14,
            marginBottom: 16,
            display: 'flex',
            gap: 10,
            flexWrap: 'wrap',
            alignItems: 'center',
          }}
        >
          <b className="small">Готовы к отгрузке (ОТК ✓):</b>
          {data.ready.map((d) => (
            <button
              key={d.id}
              className="btn sm"
              disabled={busy}
              onClick={() => act(shipStart(d.id))}
            >
              Собрать отгрузку · {d.client}
            </button>
          ))}
        </div>
      )}
      {msg && <p className="err">{msg}</p>}

      {data.ships.length === 0 && data.ready.length === 0 && (
        <p className="muted">Активных отгрузок нет.</p>
      )}

      {data.ships.map((s) => {
        const done = s.pct >= 100;
        return (
          <div className="glass" key={s.deal_id} style={{ padding: 16, marginBottom: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 11, marginBottom: 11 }}>
              <div className="grow">
                <b>{s.client}</b> <span className="mono muted small">{s.no}</span>
                <div className="rmeta">
                  {s.trucks} машин · комплектность {s.pct}%
                </div>
              </div>
              <span className={'chip ' + (done ? 'grn' : 'red')}>
                {done ? 'комплект собран' : 'недогруз ' + s.pct + '%'}
              </span>
            </div>
            <div className={'bar ' + (done ? 'ok' : 'bad')} style={{ marginBottom: 12 }}>
              <span style={{ width: s.pct + '%' }} />
            </div>
            <div className="tablewrap">
              <table className="tbl card-table">
                <thead>
                  <tr>
                    <th>Позиция</th>
                    <th className="r">По договору</th>
                    <th className="r">Отгружено</th>
                    <th className="r">Остаток</th>
                  </tr>
                </thead>
                <tbody>
                  {s.items.map((it) => {
                    const rem = it.qty_total - it.qty_shipped;
                    return (
                      <tr key={it.id} style={{ cursor: 'default' }}>
                        <td data-label="Позиция">{it.name}</td>
                        <td data-label="По договору" className="r">
                          {it.qty_total}
                        </td>
                        <td
                          data-label="Отгружено"
                          className="r"
                          style={{
                            color: it.qty_shipped >= it.qty_total ? 'var(--grn)' : 'var(--gold)',
                          }}
                        >
                          {it.qty_shipped}
                        </td>
                        <td
                          data-label="Остаток"
                          className="r"
                          style={{ color: rem > 0 ? 'var(--red)' : 'var(--mut)' }}
                        >
                          {rem > 0 ? rem : '✓'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {s.pending_trucks.length > 0 && (
              <div style={{ marginTop: 12 }}>
                <div className="rmeta" style={{ marginBottom: 6 }}>
                  Машины на КПП (ждут выпуска):
                </div>
                {s.pending_trucks.map((t) => (
                  <div
                    key={t.id}
                    className="row"
                    style={{ padding: '6px 0', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}
                  >
                    <span className="grow">
                      🚚 {t.label}
                      {t.rejected && (
                        <span className="chip red" style={{ marginLeft: 8 }}>
                          ⛔ отклонена КПП{t.reject_reason ? ': ' + t.reject_reason : ''}
                        </span>
                      )}
                    </span>
                    <button
                      className="btn ghost sm"
                      disabled={busy}
                      title="Править номер/водителя"
                      onClick={() => setEditTruck({ dealId: s.deal_id, truck: t })}
                    >
                      ✎ Править
                    </button>
                    <button
                      className="btn ghost sm"
                      disabled={busy}
                      title="Отменить машину — количества вернутся в остаток"
                      onClick={() => cancelTruck(s.deal_id, t)}
                    >
                      Отменить
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div
              style={{
                display: 'flex',
                gap: 8,
                marginTop: 12,
                flexWrap: 'wrap',
                alignItems: 'center',
              }}
            >
              {!done && (
                <button className="btn sm" disabled={busy} onClick={() => setTruck(s)}>
                  🚚 Отправить машину на КПП
                </button>
              )}
              {done && s.items.length > 0 && s.kpp_pending > 0 && (
                <span className="chip gold">На выпуск КПП: {s.kpp_pending}</span>
              )}
              {done && (s.items.length === 0 || s.kpp_pending === 0) && (
                <button
                  className="btn sm"
                  disabled={busy}
                  onClick={() => act(shipClose(s.deal_id))}
                >
                  Закрыть отгрузку ✓
                </button>
              )}
            </div>
          </div>
        );
      })}

      {truck && (
        <TruckModal
          ship={truck}
          onClose={() => setTruck(null)}
          onDone={() => {
            setTruck(null);
            refresh();
          }}
        />
      )}

      {editTruck && (
        <TruckEditModal
          dealId={editTruck.dealId}
          truck={editTruck.truck}
          onClose={() => setEditTruck(null)}
          onDone={() => {
            setEditTruck(null);
            refresh();
          }}
        />
      )}
    </div>
  );
}
